/** E14.4 Excel-Import (xlsx oder CSV) für Mieter, Regale und Artikel: Vorschau, Spalten zuordnen, Fehlerliste. Export-ZIP liegt unter Einstellungen › Abrechnung. */
import { useRef, useState, type ChangeEvent } from "react";
import { Link } from "react-router";
import { Button, Notice } from "../ui";
import { Card, PageHeader, Select } from "../ui/Layout";
import type { ImportArt } from "@shared/schemas/wachstum";
import { useImport, useImportVorschau, type ImportVorschau } from "./wachstum-api";

const ARTEN: [ImportArt, string, string][] = [
  ["mieter", "Mieter", "Name, E-Mail, Telefon, Adresse, IBAN, Mieternummer"],
  ["regale", "Regale", "Regalnummer, Klasse, Lage"],
  ["artikel", "Artikel", "Mieternummer, Regalnummer, Bezeichnung, Preis"],
];

function dateiLesen(datei: File): Promise<{ base64?: string; text?: string }> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    if (/\.xlsx$/i.test(datei.name)) {
      r.onload = () => resolve({ base64: String(r.result).split(",")[1] ?? "" });
      r.readAsDataURL(datei);
    } else {
      r.onload = () => resolve({ text: String(r.result) });
      r.readAsText(datei, "utf-8");
    }
  });
}

export function Import() {
  const [art, setArt] = useState<ImportArt>("mieter");
  const [datei, setDatei] = useState<{ name: string; inhalt: { base64?: string; text?: string } } | null>(null);
  const [vorschau, setVorschau] = useState<ImportVorschau | null>(null);
  const [zuordnung, setZuordnung] = useState<Record<string, number | null>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);
  const vorschauM = useImportVorschau();
  const importM = useImport();

  async function waehlen(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFehler(null);
    setVorschau(null);
    importM.reset();
    if (f.size > 8_000_000) return setFehler("Die Datei ist größer als 8 MB.");
    try {
      const inhalt = await dateiLesen(f);
      setDatei({ name: f.name, inhalt });
      const v = await vorschauM.mutateAsync({ art, dateiname: f.name, ...inhalt });
      setVorschau(v);
      setZuordnung(v.zuordnung);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Die Datei lässt sich nicht lesen.");
    }
  }

  return (
    <>
      <PageHeader kicker="System" title="Import" sub="Mieter, Regale oder Artikel aus Excel (xlsx) oder CSV übernehmen – erste Zeile sind die Spaltenüberschriften" />
      <Notice tone="info" title="Export">
        Alle Daten als ZIP (CSV je Tabelle, Stammdaten und Geldbewegungen) gibt es unter <Link to="/laden/einstellungen/abrechnung">Einstellungen › Abrechnung</Link>.
      </Notice>
      <Card title="1. Was wird importiert?">
        <div className="app-option-liste">
          {ARTEN.map(([k, t, sub]) => (
            <button key={k} type="button" className={["sc-option", art === k && "is-on"].filter(Boolean).join(" ")} onClick={() => { setArt(k); setVorschau(null); setDatei(null); importM.reset(); }}>
              <span className="sc-radio" /><span className="sc-col" style={{ gap: 0 }}><span className="sc-strong">{t}</span><span className="sc-muted sc-small">{sub}</span></span>
            </button>
          ))}
        </div>
      </Card>
      <Card title="2. Datei wählen">
        <input ref={eingabe} type="file" accept=".xlsx,.csv,.txt" onChange={(e) => void waehlen(e)} style={{ display: "none" }} />
        <div className="sc-toolbar">
          <Button variant="secondary" icon="plus" onClick={() => eingabe.current?.click()} loading={vorschauM.isPending}>Datei wählen</Button>
          {datei ? <span className="sc-muted">{datei.name}{vorschau ? ` · ${vorschau.anzahl} Zeilen` : ""}</span> : null}
        </div>
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        <p className="sc-muted sc-small">Vorhandene Einträge (gleiche E-Mail, gleiche Regalnummer) werden übersprungen, nichts wird überschrieben. Bis 2000 Zeilen je Datei.</p>
      </Card>
      {vorschau ? (
        <Card title="3. Spalten zuordnen">
          <div className="sc-grid-2">
            {vorschau.felder.map((f) => (
              <Select key={f.feld} label={`${f.label}${f.pflicht ? " (Pflicht)" : ""}`} value={zuordnung[f.feld] ?? ""} onChange={(e) => setZuordnung({ ...zuordnung, [f.feld]: e.target.value === "" ? null : Number(e.target.value) })}>
                <option value="">nicht importieren</option>
                {vorschau.spalten.map((sp, i) => <option key={i} value={i}>{sp || `Spalte ${i + 1}`}</option>)}
              </Select>
            ))}
          </div>
          <div className="sc-scroll" style={{ marginTop: "var(--space-4)" }}>
            <table className="rg-table">
              <thead><tr>{vorschau.spalten.map((sp, i) => <th key={i}>{sp || `Spalte ${i + 1}`}</th>)}</tr></thead>
              <tbody>{vorschau.beispiele.map((z, i) => <tr key={i}>{vorschau.spalten.map((_, j) => <td key={j}>{z[j] ?? ""}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <div className="app-form__actions">
            <Button variant="primary" icon="check" loading={importM.isPending} onClick={() => datei && importM.mutate({ art, dateiname: datei.name, ...datei.inhalt, zuordnung })}>{vorschau.anzahl} Zeilen importieren</Button>
          </div>
          {importM.isError ? <Notice tone="error">{importM.error.message}</Notice> : null}
          {importM.isSuccess ? (
            <Notice tone={importM.data.fehler.length ? "attention" : "success"} title={`${importM.data.angelegt} angelegt, ${importM.data.uebersprungen} übersprungen, ${importM.data.fehler.length} Fehler`}>
              {importM.data.fehler.length ? <ul className="sc-small" style={{ margin: 0, paddingLeft: "var(--space-4)" }}>{importM.data.fehler.slice(0, 50).map((f) => <li key={f.zeile}>Zeile {f.zeile}: {f.grund}</li>)}</ul> : "Alle Zeilen sind übernommen."}
            </Notice>
          ) : null}
        </Card>
      ) : null}
    </>
  );
}
